/* eslint-disable indent */
import { normalizePunctualityReport } from '@src/main/modules/messages/utils/formatters';
import { findDetailedPunctualityReports } from '@src/main/modules/reports/punctuality/application/use-cases/find-detailed-punctuality-reports.usecase';
import { createPunctualityReportHttpRepository } from '@src/main/modules/reports/punctuality/infrastructure/repository/PunctualityReportHttpRepository';
import {
  FormExcelItemDto,
  PunctualityDetail,
  PunctualityReportCriteria,
} from '@src/main/modules/reports/shared/domain/report.interface';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { exportToExcel } from '@src/main/shared/utils/exportToExcel';
import { useCallback, useMemo } from 'react';

const getPunctualityItemTitles = (detailedReports: PunctualityDetail[], formType: string) =>
  formType === 'NB'
    ? detailedReports
        .flatMap((form) => form.items)
        .filter((item, index, self) => self.findIndex((t) => t.title === item.title) === index)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((item) => item.title)
    : (() => {
        const allItems = detailedReports.flatMap((form) => form.items);
        const itemsByOrder = allItems.reduce(
          (acc, item) => {
            const order = item.order ?? 0;
            if (!acc[order]) acc[order] = [];
            acc[order].push(item);
            return acc;
          },
          {} as Record<number, FormExcelItemDto[]>,
        );

        return Object.keys(itemsByOrder)
          .map(Number)
          .sort((a, b) => a - b)
          .flatMap((order) =>
            itemsByOrder[order]
              .sort((a, b) => a.title.localeCompare(b.title))
              .map((item) => item.title),
          );
      })();

export const usePunctualityExport = (
  criteria: PunctualityReportCriteria,
  formType: string,
  fileName: string,
) => {
  const { user } = useAuth();

  const getToken = useCallback(async () => user?.token ?? '', [user?.token]);
  const tokenProvider = useMemo(() => ({ getToken }), [getToken]);

  const repository = useMemo(() => {
    if (!user?.token) return null;
    return createPunctualityReportHttpRepository(tokenProvider);
  }, [user?.token, tokenProvider]);

  const onExportExcel = useCallback(async () => {
    if (!repository) return;

    const detailedReports = await findDetailedPunctualityReports(repository, criteria);
    const itemTitles = getPunctualityItemTitles(detailedReports, formType);
    const normalizedData = detailedReports.map((report) =>
      normalizePunctualityReport(report, itemTitles),
    );
    await exportToExcel(normalizedData, fileName);
  }, [repository, criteria, formType, fileName]);

  return useMemo(() => ({ onExportExcel }), [onExportExcel]);
};
